import React, { useMemo, ReactNode } from "react";

import { CpSelectInner } from "./cp-select-inner.component";
import { TriggerMultiInput } from "./triggers/trigger-multi-input.component";
import { TriggerSingle } from "./triggers/trigger-single.component";
import { ItemMulti } from "./items/item-multi.component";
import { ItemSingle } from "./items/item-single.component";
import { Group } from "./common/group.component";
import {
  buildData,
  buildGroupData,
  filterAlpha,
  SelectDataItem,
  SelectGroupData,
} from "./common/utils";
import { TriggerPills } from "./triggers/trigger-pills.component";
import { TriggerMulti } from "./triggers/trigger-multi.component";
import { Footer } from "./common/footer.component";

export type CpSelectProps<TData, TValue> = {
  allowTextWrap?: boolean;
  allowContentClicks?: boolean;
  autoSelectOnSearch?: boolean;
  contentWidth?: number | "sm" | "md" | "lg" | "block" | "auto";
  position?:
    | "auto"
    | "top"
    | "top-start"
    | "top-end"
    | "right"
    | "right-start"
    | "right-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "left"
    | "left-start"
    | "left-end";
  renderGroup?: (props: any) => ReactNode;
  preventKeyOpen?: boolean;
  data?: TData[];
  isGroupData?: boolean;
  groupType?: "header" | "border";
  searchFilter?: boolean | ((item: SelectDataItem) => boolean);
  searchValue?: string;
  searchPlaceholder?: string;
  clearOnSelect?: boolean;
  transformData?: (item: TValue) => SelectDataItem;
  value: TValue | TValue[];
  renderEmptyState?: (params: any) => ReactNode;
  emptyStateAction?: (params: {
    onChange: (value: TValue | TValue[]) => void;
    close: () => void;
  }) => void;
  triggerRef?: React.RefObject<any>;
  // Additional props from CpSelectInner
  appendTo?: HTMLElement | "parent";
  className?: string;
  clearable?: boolean;
  cover?: boolean;
  disabled?: boolean;
  disableSelection?: boolean;
  insertSearch?: boolean;
  loading?: boolean;
  onBlur?: () => void;
  onChange: (value: TValue | TValue[]) => void;
  onClose?: () => void;
  onOpen?: () => void;
  placeholder?: string;
  preventOutsideClickUntilClosed?: boolean;
  renderFooter?: any;
  renderItem?: (props: any) => ReactNode;
  renderTrigger?: (props: any) => ReactNode;
  searchOnChange?: (value: string) => void;
  showResultsInContent?: boolean;
  stopPropagation?: boolean;
  style?: React.CSSProperties;
  triggerIsBlock?: boolean;
  contentHeight?: string | number;
  insertDone?: boolean;
};

export type CpSelectComponent = (<TData, TValue>(
  props: CpSelectProps<TData, TValue>,
) => React.JSX.Element) & {
  TriggerMulti: typeof TriggerMulti;
  TriggerMultiInput: typeof TriggerMultiInput;
  TriggerSingle: typeof TriggerSingle;
  TriggerPills: typeof TriggerPills;
  ItemMulti: typeof ItemMulti;
  ItemSingle: typeof ItemSingle;
  Group: typeof Group;
  Footer: typeof Footer;
  filterAlpha: typeof filterAlpha;
};

function CpSelectBase<TData, TValue>({
  allowTextWrap = false,
  allowContentClicks = true,
  autoSelectOnSearch = false,
  contentWidth = "lg",
  position = "bottom",
  renderGroup = Group,
  preventKeyOpen = false,
  data: _data,
  isGroupData,
  groupType = "header",
  searchFilter,
  searchValue,
  searchPlaceholder,
  clearOnSelect,
  transformData,
  value: rawValue,
  renderEmptyState,
  emptyStateAction,
  triggerRef,
  ...rest
}: CpSelectProps<TData, TValue>) {
  const isSearch = !!(searchFilter && searchValue);

  const { isMulti, valueTransformed } = useMemo(() => {
    const isMulti = Array.isArray(rawValue);
    return {
      isMulti,
      valueTransformed: (isMulti
        ? transformData
          ? rawValue.map(transformData)
          : rawValue
        : rawValue && (transformData ? transformData(rawValue) : rawValue)) as
        | SelectDataItem
        | SelectDataItem[],
    };
    /* Add missing deps and verify it doesn't break: transformData (needs to be memoized or stored in a ref) */
  }, [rawValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, dataMap } = useMemo(() => {
    if (!_data) return { data: [], dataMap: {} };
    return isGroupData
      ? buildGroupData(
          _data as SelectGroupData[],
          transformData,
          searchFilter as any,
          searchValue,
        )
      : buildData(
          _data as any[],
          transformData,
          searchFilter as any,
          searchValue,
        );
    /* Add missing deps and verify it doesn't break: isGroupData */
  }, [_data, searchValue, transformData, searchFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CpSelectInner<TData, TValue>
      allowContentClicks={allowContentClicks}
      allowTextWrap={allowTextWrap}
      autoSelectOnSearch={autoSelectOnSearch}
      contentWidth={contentWidth}
      position={position}
      renderGroup={renderGroup}
      preventKeyOpen={preventKeyOpen}
      data={data}
      dataMap={dataMap}
      isGroupData={!!isGroupData}
      groupType={groupType}
      isMulti={isMulti}
      isSearch={isSearch}
      searchFilter={searchFilter}
      searchValue={searchValue}
      searchPlaceholder={searchPlaceholder}
      clearOnSelect={clearOnSelect}
      transformData={transformData}
      value={rawValue}
      valueTransformed={valueTransformed}
      renderEmptyState={renderEmptyState}
      emptyStateAction={emptyStateAction}
      triggerRef={triggerRef}
      {...rest}
    />
  );
}

// Create typed component with static properties
const CpSelect = CpSelectBase as CpSelectComponent;

// triggers
CpSelect.TriggerMulti = TriggerMulti;
CpSelect.TriggerMultiInput = TriggerMultiInput;
CpSelect.TriggerSingle = TriggerSingle;
CpSelect.TriggerPills = TriggerPills;

// items
CpSelect.ItemMulti = ItemMulti;
CpSelect.ItemSingle = ItemSingle;

// group
CpSelect.Group = Group;

// footer
CpSelect.Footer = Footer;

// utils
CpSelect.filterAlpha = filterAlpha;

// Export the properly typed component
export { CpSelect };
